
import { Loader2 } from "lucide-react";
import { Link } from "./router";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";

import { cn } from "./cn";
import styles from "./Button.module.css";
import { Tooltip } from "./Tooltip";

export type ButtonVariant =
  | "primary"
  | "default"
  | "subtle"
  | "outline"
  | "danger"
  | "dangerSubtle"
  | "warning";

export type ButtonSize = "sm" | "md" | "lg";

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: styles.variantPrimary,
  default: styles.variantDefault,
  subtle: styles.variantSubtle,
  outline: styles.variantOutline,
  danger: styles.variantDanger,
  dangerSubtle: styles.variantDangerSubtle,
  warning: styles.variantWarning,
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  fullWidth?: boolean;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "default",
    size = "md",
    loading = false,
    iconBefore,
    iconAfter,
    fullWidth,
    className,
    children,
    disabled,
    type,
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={cn(
        styles.button,
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        fullWidth && styles.fullWidth,
        loading && styles.loading,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span className={styles.label}>
        {iconBefore}
        <span className={styles.labelText}>{children}</span>
        {iconAfter}
      </span>
      {loading ? (
        <span className={styles.spinner} aria-hidden="true">
          <Loader2 size={16} strokeWidth={1.75} className={styles.spin} />
        </span>
      ) : null}
    </button>
  );
});

export type LinkButtonProps = {
  href: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconBefore?: ReactNode;
  iconAfter?: ReactNode;
  fullWidth?: boolean;
  className?: string;
  download?: boolean | string;
  target?: string;
  rel?: string;
  children: ReactNode;
};

export function LinkButton({
  href,
  variant = "default",
  size = "md",
  iconBefore,
  iconAfter,
  fullWidth,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <Link
      href={href}
      prefetch={rest.download ? false : undefined}
      className={cn(
        styles.button,
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        fullWidth && styles.fullWidth,
        className,
      )}
      {...rest}
    >
      <span className={styles.label}>
        {iconBefore}
        <span className={styles.labelText}>{children}</span>
        {iconAfter}
      </span>
    </Link>
  );
}

export type IconButtonProps = Omit<ButtonProps, "children" | "iconBefore" | "iconAfter"> & {
  label: string;
  icon: ReactNode;
  tooltip?: ReactNode;
  showTooltip?: boolean;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, tooltip, showTooltip = true, variant = "subtle", size = "md", className, loading = false, disabled, ...rest },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      className={cn(
        styles.button,
        styles.iconButton,
        VARIANT_CLASS[variant],
        SIZE_CLASS[size],
        loading && styles.loading,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      <span className={styles.label}>{icon}</span>
      {loading ? (
        <span className={styles.spinner} aria-hidden="true">
          <Loader2 size={16} strokeWidth={1.75} className={styles.spin} />
        </span>
      ) : null}
    </button>
  );

  if (!showTooltip) return button;

  return <Tooltip content={tooltip ?? label}>{button}</Tooltip>;
});
